import React from 'react';
import { motion } from 'framer-motion';
import './Features.css';
import BlockhatAuditBanner from '../BlockhatAuditBanner/BlockhatAuditBanner';

// Import SVG icons
import { ReactComponent as GlobalIcon } from '../../assets/icons/global.svg';

interface Feature {
  icon: () => React.ReactElement;
  title: string;
  description: string;
  benefits: string[];
}

const Features: React.FC = () => {
  const features: Feature[] = [
    {
      icon: () => <img src="/contact.png" className="icon" alt="Instant Transactions" />,
      title: 'Instant Transactions',
      description: 'Lightning-fast transfers with near-zero confirmation times powered by advanced blockchain technology.',
      benefits: ['<1s transaction speed', 'Real-time settlement', '24/7 global availability']
    },
    {
      icon: () => <img src="/securityicon.png" className="icon" alt="Security" />,
      title: 'Bank-Grade Security',
      description: 'Multi-layered security architecture ensuring your digital assets are protected at all times.',
      benefits: ['256-bit encryption', 'Multi-signature wallets', 'Regulatory compliance']
    },
    {
      icon: () => <img src="/transactionicon.png" className="icon" alt="Stable Value" />,
      title: 'Stable Value',
      description: 'Always pegged 1:1 to USD with transparent reserves and regular audits for maximum stability.',
      benefits: ['1:1 USD backing', 'Transparent reserves', 'Regular audits']
    },
    {
      icon: () => <GlobalIcon className="icon" />,
      title: 'Global Accessibility',
      description: 'Borderless payments enabling seamless transactions anywhere in the world, anytime.',
      benefits: ['Worldwide acceptance', 'Cross-border payments', 'Multi-platform support']
    },
    {
      icon: () => <img src="/gasicon.png" className="icon" alt="Zero Transaction Fees" />,
      title: 'Zero Transaction Fees',
      description: 'Cost-effective digital payments with zero fees, making micro-transactions viable.',
      benefits: ['0% transaction fee', 'No hidden charges', 'Volume discounts available']
    },
    {
      icon: () => <img src="/integrationicon.png" className="icon" alt="DeFi Integration" />,
      title: 'DeFi Integration',
      description: 'Seamlessly integrate with DeFi protocols for lending, staking, and yield farming opportunities.',
      benefits: ['DeFi compatibility', 'Yield opportunities', 'Liquidity provision']
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section id="features" className="features section-padding">
      <div className="container">
        <motion.div
          className="features-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
      
          <h2 className="section-title">
            Built for Next-Gen<br />
            <span className="title-gradient">Financial Infrastructure</span>
          </h2>
          <p className="section-subtitle">
            E-Pay delivers enterprise-grade stability, transparency, and performance
            with cutting-edge blockchain technology designed for modern finance.
          </p>
        </motion.div>

        <motion.div
          className="features-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="feature-card modern-card"
              variants={itemVariants}
              whileHover={{
                scale: 1.02,
                y: -8,
                transition: { duration: 0.3 }
              }}
            >
              <div className="feature-header">
                <div className="feature-icon">
                  {feature.icon()}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
              </div>
              <div className="feature-content">
                <p className="feature-description">{feature.description}</p>
                <ul className="feature-benefits">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="benefit-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="check-icon">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="features-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <h3 className="cta-title">$1,000,000,000.00.- Dollar Collateral</h3>
            <p className="cta-description">
            Treasury notes and cash deposits as collateral,<br/ > Real time proof of reserves. <br/ >Audited and attested reserves by 3rd party auditors.<br />
              
            </p>
            <div className="cta-actions">
              <motion.button
                className="btn btn-primary btn-large"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View Transparency
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12H19M19 12L12 5M19 12L12 19"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </motion.button>
              <motion.button
                className="btn btn-ghost btn-large"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Documentation
              </motion.button>
            </div>
          </div>
          <div className="cta-stats">
            <div className="stat-item">
              <div className="stat-number">$1B+</div>
              <div className="stat-label">Attested Assets</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">32K+</div>
              <div className="stat-label">Active Users</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">99.99%</div>
              <div className="stat-label">Uptime</div>
            </div>
          </div>
        </motion.div>
      </div>
      
      <div className="features-background">
        <div className="bg-pattern"></div>
        <div className="bg-glow glow-1"></div>
        <div className="bg-glow glow-2"></div>
      </div>

      {/* Blockhat Solana Audit Banner */}
      <BlockhatAuditBanner />
    </section>
  );
};

export default Features;